/**
 * Edit-message hook with optimistic UI + 15-min window enforcement.
 *
 *   const { edit, canEdit, isPending, error } = useEditMessage()
 *   if (canEdit(message)) await edit(message.id, "new body")
 *
 * Flow:
 *   1. Look up the message in the store for an undo snapshot.
 *   2. `replaceWireMessage` with optimistic `body + editedAt = now()`.
 *   3. Push `message:edit` over the conversation channel.
 *   4. Failure → `replaceWireMessage` with the snapshot (rollback).
 *
 * `canEdit(message)` guards the Edit affordance: sender, text-only, not
 * deleted, 15-min window. The rule itself lives in `isEditable`, exported so
 * the message bubble's menu asks the identical question of its own
 * (differently shaped) message object. The server checks only that the
 * editor is the sender (`not_sender` in the messenger channel); the window,
 * the text-only rule and the deleted check exist nowhere else. An earlier
 * version of this comment claimed the server returned EDIT_WINDOW_EXPIRED;
 * nothing does. Treat the window as presentation, not a boundary.
 */

import { useCallback, useState } from 'react'
import { useAuthStore } from '@/features/auth/store/auth.store'
import type { Message as WireMessage } from '../services/messenger.wire'
import { editMessageOnConversation } from '../services/messenger.socket'
import { useMessengerStore } from '../store/messenger.store'

/** D4: must match the server's `EDIT_WINDOW_MS` in `message.service.ts`.
 *  Exported so `useDeleteMessage` can hold "delete for everyone" to the same
 *  window without a second copy of the number drifting from this one. */
export const EDIT_WINDOW_MS = 15 * 60 * 1000

/**
 * The rule Edit is offered under, shared with the bubble menu so it never
 * shows an action that saving would then refuse. Takes the fields the wire
 * message and the UI message both carry, rather than either shape itself, so
 * neither side has to build the other just to ask the question.
 */
export function isEditable(
  message: { isSentByMe: boolean; type?: string; isDeleted?: boolean; createdAt?: string },
  now: number = Date.now(),
): boolean {
  if (!message.isSentByMe) return false
  if (message.type !== undefined && message.type !== 'text') return false
  if (message.isDeleted) return false
  if (!message.createdAt) return false
  return now - new Date(message.createdAt).getTime() <= EDIT_WINDOW_MS
}

interface UseEditMessage {
  edit: (messageId: string, body: string) => Promise<WireMessage | null>
  canEdit: (message: WireMessage) => boolean
  isPending: boolean
  error: string | null
}

/** Walk every loaded thread for a message. O(N total messages); good enough
 *  since the UI usually edits from a currently-visible thread. */
function findMessage(messageId: string): WireMessage | undefined {
  const lists = useMessengerStore.getState().wireMessagesByConv
  for (const list of lists.values()) {
    const m = list.find((x) => x.id === messageId)
    if (m) return m
  }
  return undefined
}

export function useEditMessage(): UseEditMessage {
  const me = useAuthStore((s) => s.user)
  // "Am I the sender?" compares against senderId, which is the contactPresenceId.
  // The socket edit push still authenticates as the auth user (`me.id`).
  const presenceId = useAuthStore((s) => s.presenceId)
  const replace = useMessengerStore((s) => s.replaceWireMessage)

  const [isPending, setIsPending] = useState<boolean>(false)
  const [error, setError]         = useState<string | null>(null)

  const canEdit = useCallback(
    (message: WireMessage): boolean => {
      if (!presenceId) return false
      return isEditable({
        isSentByMe: message.senderId === presenceId,
        type: message.type,
        isDeleted: !!message.deletedAt,
        createdAt: message.createdAt,
      })
    },
    [presenceId],
  )

  const edit = useCallback(
    async (messageId: string, body: string): Promise<WireMessage | null> => {
      const snapshot = findMessage(messageId)
      if (!snapshot) {
        setError('message not found')
        return null
      }
      if (!canEdit(snapshot)) {
        setError('cannot edit this message')
        return null
      }

      if (!me?.id) { setError('not authenticated'); return null }

      setIsPending(true)
      setError(null)

      // Optimistic — server's edited_at is generated server-side; using
      // local now() is good enough until the server response lands.
      const nowIso = new Date().toISOString()
      const edited: WireMessage = { ...snapshot, body, editedAt: nowIso }
      replace(edited)

      try {
        await editMessageOnConversation(me.id, snapshot.conversationId, messageId, body, me.name)
        return edited
      } catch (err: unknown) {
        replace(snapshot)
        setError(err instanceof Error ? err.message : 'failed to edit')
        return null
      } finally {
        setIsPending(false)
      }
    },
    [canEdit, replace, me?.id, me?.name],
  )

  return { edit, canEdit, isPending, error }
}
